import { useEffect, useRef, useState } from 'react';

const useSSENotifications = (userId, token) => {
  const [isConnected, setIsConnected] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const eventSourceRef = useRef(null);

  useEffect(() => {
    if (!userId || !token) return;
    let disposed = false;
    let retryTimer = null;
    let failures = 0;

    // Get backend URL for SSE connection
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
    
    const connectSSE = () => {
      if (disposed) return;
      try {
        // Close existing connection
        if (eventSourceRef.current) {
          eventSourceRef.current.close();
        }

        // Create new SSE connection with auth header
        const url = `${BACKEND_URL}/api/notifications/stream/${userId}`;
        
        // Unfortunately, EventSource doesn't support custom headers directly
        // We'll need to pass the token as a query parameter for authentication
        const urlWithAuth = `${url}?token=${encodeURIComponent(token)}`;
        
        eventSourceRef.current = new EventSource(urlWithAuth);

        eventSourceRef.current.onopen = (event) => {
          if (disposed) return;
          failures = 0;
          console.log('SSE connection opened for user:', userId);
          setIsConnected(true);
        };

        eventSourceRef.current.onmessage = (event) => {
          if (disposed) return;
          try {
            const message = JSON.parse(event.data);
            console.log('SSE message received:', message);

            if (message.type === 'duel_challenge') {
              // Add new notification
              setNotifications(prev => [...prev, {
                id: `${message.duel_id}_${Date.now()}`,
                ...message,
                timestamp: new Date()
              }]);
            } else if (message.type === 'connected') {
              console.log('SSE connection confirmed:', message.message);
            } else if (message.type === 'heartbeat') {
              console.log('SSE heartbeat received');
            }
          } catch (error) {
            console.error('Error parsing SSE message:', error);
          }
        };

        eventSourceRef.current.onerror = () => {
          if (disposed) return;
          setIsConnected(false);
          // Disable native auto-retry before scheduling ours: exactly one connection/timer.
          eventSourceRef.current?.close();
          clearTimeout(retryTimer);
          failures += 1;
          if (failures === 3) console.warn('Notificaciones temporalmente desconectadas; reintentando conexión.');
          retryTimer = setTimeout(connectSSE, Math.min(3000 * 2 ** Math.min(failures - 1, 4), 30000));
        };

        eventSourceRef.current.onclose = (event) => {
          console.log('SSE connection closed');
          setIsConnected(false);
        };

      } catch (error) {
        console.error('Failed to create SSE connection:', error);
        setIsConnected(false);
      }
    };

    connectSSE();

    // Cleanup on unmount
    return () => {
      disposed = true;
      clearTimeout(retryTimer);
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      setIsConnected(false);
    };
  }, [userId, token]);

  const removeNotification = (notificationId) => {
    setNotifications(prev => prev.filter(n => n.id !== notificationId));
  };

  const clearAllNotifications = () => {
    setNotifications([]);
  };

  return {
    isConnected,
    notifications,
    removeNotification,
    clearAllNotifications
  };
};

export default useSSENotifications;